import { absolutePosition } from "../auto-fuse";
import { planTerminalSwaps } from "../busbar-stud-swaps";
import { getDefinition } from "../catalog";
import { layoutSelectedComponents } from "../component-auto-layout";
import { findCable, findNode } from "../system-lookup";
import { optimalTerminalOverrides } from "../terminal-snap";
import type { System, SystemNode, TerminalSide } from "../types";
import type { CableRouter } from "./types";

export type TerminalOverrideUpdate = {
  id: string;
  overrides: Record<string, { side: TerminalSide; offset: number }>;
};

export function terminalOverrideUpdates(
  system: System,
  cableIds: Iterable<string>,
): TerminalOverrideUpdate[] {
  const nodeIds = new Set<string>();
  for (const cableId of cableIds) {
    const cable = findCable(system, cableId);
    if (!cable) continue;
    nodeIds.add(cable.fromNodeId);
    nodeIds.add(cable.toNodeId);
  }
  const updates: TerminalOverrideUpdate[] = [];
  for (const nodeId of nodeIds) {
    const node = findNode(system, nodeId);
    if (!node || node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) continue;
    const overrides = optimalTerminalOverrides({
      node,
      def,
      nodes: system.nodes,
      cables: system.cables,
      newAbsPos: absolutePosition(node, system.nodes),
    });
    if (Object.keys(overrides).length > 0) updates.push({ id: nodeId, overrides });
  }
  return updates;
}

export function applyTerminalOverrideUpdates(
  nodes: SystemNode[],
  updates: TerminalOverrideUpdate[],
): SystemNode[] {
  if (updates.length === 0) return nodes;
  const byId = new Map(updates.map((u) => [u.id, u]));
  return nodes.map((n) => {
    const u = byId.get(n.id);
    if (!u || n.kind !== "component") return n;
    return {
      ...n,
      terminalOverrides: { ...(n.terminalOverrides ?? {}), ...u.overrides },
    };
  });
}

/** Produces one immutable edit: place components, aim movable terminals, then
 * route against that final geometry. The caller owns undo and edit permissions. */
export class SchematicLayoutEngine {
  constructor(private readonly router: CableRouter) {}

  route(system: System, ids: string[]): System {
    if (ids.length === 0) return system;
    const swaps = planTerminalSwaps(system.nodes, system.cables, ids);
    const swappedCables = swaps.size === 0 ? system.cables : system.cables.map((cable) => {
      const update = swaps.get(cable.id);
      return update ? { ...cable, ...update } : cable;
    });
    const updates = terminalOverrideUpdates({ ...system, cables: swappedCables }, ids)
      .filter((update) => {
        const node = findNode(system, update.id);
        return node?.kind === "component" && Object.entries(update.overrides).some(([id, next]) => {
          const current = node.terminalOverrides?.[id];
          return current?.side !== next.side || current?.offset !== next.offset;
        });
      });
    // Aiming after routing would leave bends attached to the old terminal
    // positions, which endpoint rectification can turn into backtracks.
    const nodes = applyTerminalOverrideUpdates(system.nodes, updates);
    const routes = this.router.settle(nodes, swappedCables, ids);
    let cablesChanged = false;
    const cables = swappedCables.map((cable, index) => {
      const points = routes.get(cable.id);
      const original = system.cables[index];
      const endpointsChanged = cable.fromTerminalId !== original.fromTerminalId ||
        cable.toTerminalId !== original.toTerminalId;
      if (!points && !endpointsChanged) return original;
      const previous = original.waypoints ?? [];
      const next = points ?? [];
      const sameRoute = previous.length === next.length && previous.every((point, i) =>
        point.x === next[i].x && point.y === next[i].y);
      if (!endpointsChanged && sameRoute) return original;
      cablesChanged = true;
      if (next.length > 0) return { ...cable, waypoints: next };
      const { waypoints: _waypoints, ...bare } = cable;
      return bare;
    });
    if (nodes === system.nodes && !cablesChanged) return system;
    return { ...system, nodes, cables: cablesChanged ? cables : system.cables };
  }

  arrange(system: System, ids: string[]): System {
    const nodes = layoutSelectedComponents(system.nodes, system.cables, ids);
    if (nodes === system.nodes) return system;
    return this.route({ ...system, nodes }, system.cables.map((cable) => cable.id));
  }
}
